import React, { useCallback, useEffect, useState } from 'react';
import { MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline';
import { usePageTitle } from '../hooks/usePageTitle';
import { drillsAPI } from '../services/api';
import { DRILL_CATEGORIES, Drill } from '../types/drill';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { isStaff } from '../types/auth';
import DrillThumbnail from '../components/drills/DrillThumbnail';
import DrillDetailModal from '../components/drills/DrillDetailModal';
import DrillFormModal from '../components/drills/DrillFormModal';
import DeleteConfirmModal from '../components/ui/DeleteConfirmModal';
import TemplatesTab from '../components/drills/TemplatesTab';
import { getErrorMessage } from '../utils/errorMessage';

/**
 * Gyakorlattár: a stáb közös, rajzos gyakorlatai. Innen lehet őket az
 * edzéstervekbe beemelni (az edzés "Edzésterv" részénél).
 */
const Drills: React.FC = () => {
  usePageTitle('Gyakorlattár');
  const { showToast } = useToast();
  const { user } = useAuth();
  const staff = isStaff(user);

  const [drills, setDrills] = useState<Drill[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [onlyMine, setOnlyMine] = useState(false);

  const [viewing, setViewing] = useState<Drill | null>(null);
  const [editing, setEditing] = useState<Drill | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [deleting, setDeleting] = useState<Drill | null>(null);
  const [tab, setTab] = useState<'drills' | 'templates'>(() => {
    try {
      return localStorage.getItem('drills-tab') === 'templates' ? 'templates' : 'drills';
    } catch {
      return 'drills';
    }
  });

  const switchTab = (next: 'drills' | 'templates') => {
    setTab(next);
    try {
      localStorage.setItem('drills-tab', next);
    } catch {
      // privát módban nincs tárhely - a fül ettől még vált
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setDrills(
        await drillsAPI.getAll({
          search: search || undefined,
          category: category || undefined,
          onlyMine: onlyMine || undefined,
        })
      );
    } catch (err) {
      console.error('A gyakorlattár betöltése nem sikerült:', err);
    } finally {
      setLoading(false);
    }
  }, [search, category, onlyMine]);

  useEffect(() => {
    if (!staff) return;
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load, staff]);

  const openNew = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (drill: Drill) => {
    setViewing(null);
    setEditing(drill);
    setFormOpen(true);
  };

  const handleSaved = (drill: Drill) => {
    setFormOpen(false);
    setEditing(null);
    showToast('Gyakorlat elmentve.');
    load();
    setViewing(drill);
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await drillsAPI.delete(deleting.id);
      showToast('Gyakorlat törölve.');
      setDeleting(null);
      setViewing(null);
      load();
    } catch (err: any) {
      showToast(getErrorMessage(err, 'A törlés nem sikerült.'), 'error');
    }
  };

  if (!staff) {
    return (
      <div className="rounded-lg bg-white py-12 text-center shadow">
        <p className="text-gray-500">A gyakorlattár a stáb (edzők, adminok) számára érhető el.</p>
      </div>
    );
  }

  const drillsTab = (
    <>
      <div className="mb-6 space-y-3 rounded-lg bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Keresés név vagy leírás alapján..."
              aria-label="Keresés a gyakorlattárban"
              className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={onlyMine}
              onChange={(e) => setOnlyMine(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Csak a saját gyakorlataim
          </label>
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {['', ...DRILL_CATEGORIES].map((c) => (
            <button
              key={c || 'all'}
              type="button"
              onClick={() => setCategory(c)}
              aria-pressed={category === c}
              className={`shrink-0 rounded-full border px-3 py-1 text-sm ${
                category === c ? 'border-primary-600 bg-primary-600 text-white' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {c || 'Mind'}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex h-48 items-center justify-center text-gray-500">Betöltés...</div>
      ) : drills.length === 0 ? (
        <div className="rounded-lg bg-white py-12 text-center shadow">
          <p className="text-gray-500">
            {search || category || onlyMine ? 'Nincs a szűrésnek megfelelő gyakorlat.' : 'Még üres a gyakorlattár.'}
          </p>
          <p className="mt-1 text-sm text-gray-400">
            Vegyél fel egy gyakorlatot, vagy egy edzésterv blokkjánál használd a „Mentés a gyakorlattárba” gombot.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {drills.map((drill) => (
            <button
              key={drill.id}
              type="button"
              onClick={() => setViewing(drill)}
              className="flex flex-col overflow-hidden rounded-lg bg-white text-left shadow transition hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <DrillThumbnail diagram={drill.diagram} title={drill.title} className="rounded-none rounded-t-lg" />
              <div className="flex flex-1 flex-col gap-1 p-3">
                <p className="font-medium text-gray-900 line-clamp-2">{drill.title}</p>
                <div className="flex flex-wrap items-center gap-1.5">
                  {drill.category && (
                    <span className="rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-800">{drill.category}</span>
                  )}
                  {drill.durationMinutes ? (
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">{drill.durationMinutes} perc</span>
                  ) : null}
                  {drill.players && <span className="truncate text-xs text-gray-500">{drill.players}</span>}
                </div>
                <p className="mt-auto pt-1 text-xs text-gray-400">{drill.createdByName}</p>
              </div>
            </button>
          ))}
        </div>
      )}

    </>
  );

  return (
    <div>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gyakorlattár</h1>
          <p className="text-sm text-gray-500">
            A stáb közös gyakorlatai és teljes edzéssablonjai - rajzzal, edzéstervbe emelhetően.
          </p>
        </div>
        {tab === 'drills' && (
          <button
            type="button"
            onClick={openNew}
            className="inline-flex items-center justify-center rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700"
          >
            <PlusIcon className="mr-2 h-5 w-5" />
            Új gyakorlat
          </button>
        )}
      </div>

      <div className="mb-4 flex gap-1 border-b border-gray-200" role="tablist">
        {([
          ['drills', 'Gyakorlatok'],
          ['templates', 'Edzéssablonok'],
        ] as const).map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() => switchTab(key)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${
              tab === key ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'templates' ? <TemplatesTab /> : drillsTab}

      {viewing && (
        <DrillDetailModal
          drill={viewing}
          onClose={() => setViewing(null)}
          onEdit={viewing.canModify ? () => openEdit(viewing) : undefined}
          onDelete={viewing.canModify ? () => setDeleting(viewing) : undefined}
        />
      )}

      {formOpen && (
        <DrillFormModal
          drill={editing}
          onClose={() => {
            setFormOpen(false);
            setEditing(null);
          }}
          onSaved={handleSaved}
        />
      )}

      <DeleteConfirmModal
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title="Gyakorlat törlése"
        message="Biztosan törlöd ezt a gyakorlatot a gyakorlattárból? A már elkészült edzéstervekben megmarad."
        itemName={deleting?.title ?? ''}
      />
    </div>
  );
};

export default Drills;
